<template>
  <AdminModal
    :is-open="isOpen"
    modalSize="lg"
    :modal-title="`${this.selectedItem ? '접속코드 상세' : '접속코드 등록'}`"
    :close="close"
    :loading="loading"
    :confirmButtonTitle="`${this.selectedItem ? '수정' : '등록'}`"
    :confirm="save"
    name="teamLeaderAccessCodeModal"
  >
    <b-container>
      <b-row>
        <b-col cols="12">
          <label for="code-name">코드명</label>
          <b-form-input
            id="code-name"
            placeholder="코드명을 입력해주세요."
            v-model="name"
            maxlength="100"
          />
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col cols="12">
          <label for="code">코드</label>
          <b-form-input
            id="code"
            placeholder="코드를 입력해주세요."
            v-model="code"
            maxlength="50"
          />
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col cols="12">
          <label>활성화 상태</label>
          <b-form-radio-group
            v-model="isActive"
            :options="activeOptions"
            class="mb-3"
            value-field="value"
            text-field="text"
          />
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col cols="6">
          <label for="start-date">시작일</label>
          <b-form-input
            id="start-date"
            type="date"
            v-model="startDate"
          />
          <small class="text-muted">비어있으면 즉시 시작됩니다.</small>
        </b-col>
        <b-col cols="6">
          <label for="end-date">종료일</label>
          <b-form-input
            id="end-date"
            type="date"
            v-model="endDate"
          />
          <small class="text-muted">비어있으면 무기한입니다.</small>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col cols="12">
          <label for="memo">메모</label>
          <b-form-textarea
            id="memo"
            v-model="memo"
            placeholder="메모를 입력해주세요. (관리용)"
            rows="3"
            max-rows="6"
          />
        </b-col>
      </b-row>

      <b-row v-if="selectedItem" class="mt-3">
        <b-col cols="6">
          <label>생성일</label>
          <div class="text-muted">{{ getDate(selectedItem.createdAt) }}</div>
        </b-col>
        <b-col cols="6">
          <label>수정일</label>
          <div class="text-muted">{{ getDate(selectedItem.updatedAt) }}</div>
        </b-col>
      </b-row>
    </b-container>
  </AdminModal>
</template>

<script>
import AdminModal from "@/components/modals/AdminModal.vue";
import customDate from "@/util/custom-date";
import api from "@/api/api";

export default {
  name: "TeamLeaderAccessCodeModal",
  components: { AdminModal },
  data() {
    return {
      id: null,
      name: "",
      code: "",
      isActive: true,
      startDate: "",
      endDate: "",
      memo: "",
      loading: false,
      activeOptions: [
        { value: true, text: '활성화' },
        { value: false, text: '비활성화' },
      ]
    }
  },
  props: ['isOpen', 'close', 'reload', 'selectedItem'],
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initializeData()
      }
    },
    selectedItem(newVal) {
      if (this.isOpen) {
        this.initializeData()
      }
    }
  },
  mounted() {
    if (this.isOpen) {
      this.initializeData()
    }
  },
  methods: {
    initializeData() {
      if (this.selectedItem) {
        // 수정 모드: 기존 데이터 로드
        this.id = this.selectedItem.id
        this.name = this.selectedItem.name || ""
        this.code = this.selectedItem.code || ""
        this.isActive = this.selectedItem.isActive ?? this.selectedItem.active
        this.startDate = this.formatDateForInput(this.selectedItem.startedAt ?? this.selectedItem.startDate)
        this.endDate = this.formatDateForInput(this.selectedItem.endedAt ?? this.selectedItem.endDate)
        this.memo = this.selectedItem.memo || ""
      } else {
        // 생성 모드: 폼 초기화
        this.resetForm()
      }
    },

    resetForm() {
      this.id = null
      this.name = ""
      this.code = ""
      this.isActive = true
      this.startDate = ""
      this.endDate = ""
      this.memo = ""
    },

    getDate(date) {
      return customDate.stringFormat(date)
    },

    formatDateForInput(dateString) {
      if (!dateString) return ""
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    validateForm() {
      if (!this.name.trim()) {
        this.$toast.error('코드명을 입력해주세요.', {
          position: "top-right",
          duration: 2000
        })
        return false
      }

      if (!this.code.trim()) {
        this.$toast.error('코드를 입력해주세요.', {
          position: "top-right",
          duration: 2000
        })
        return false
      }

      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate)
        const end = new Date(this.endDate)
        if (start >= end) {
          this.$toast.error('종료일은 시작일보다 늦어야 합니다.', {
            position: "top-right",
            duration: 2000
          })
          return false
        }
      }

      return true
    },

    save() {
      if (!this.validateForm()) return

      this.loading = true

      const requestData = {
        name: this.name.trim(),
        code: this.code.trim(),
        isActive: this.isActive,
        startDate: this.startDate || null,
        endDate: this.endDate || null,
        memo: this.memo.trim() || null
      }

      if (this.selectedItem) {
        // 수정
        api.teamLeaderAccessCode.update({
          id: this.id,
          ...requestData
        }, (response) => {
          this.loading = false
          this.$toast.success('접속코드가 수정되었습니다.', {
            position: "top-right",
            duration: 2000
          })
          this.reload()
          this.close()
        }, (err) => {
          this.loading = false
          console.log(err)
          this.$toast.error(err.response?.data?.message || '수정에 실패했습니다.', {
            position: "top-right",
            duration: 2000
          })
        })
      } else {
        // 생성
        api.teamLeaderAccessCode.create(requestData, (response) => {
          this.loading = false
          this.$toast.success('접속코드가 등록되었습니다.', {
            position: "top-right",
            duration: 2000
          })
          this.reload()
          this.close()
        }, (err) => {
          this.loading = false
          console.log(err)
          this.$toast.error(err.response?.data?.message || '등록에 실패했습니다.', {
            position: "top-right",
            duration: 2000
          })
        })
      }
    }
  }
}
</script>

<style scoped>
label {
  font-weight: 500;
  margin-bottom: 0.25rem;
}
</style>
